import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "redux/store";
import { USER_LOGOUT } from "redux/system.action";
import RequestStatus from "redux/slices/RequestStatus";
import { Cell, Form, FormField, GetFullFormDocument, GetReviewConfigDocument, ReviewConfig } from "generated/graphql";
import { runGqlQuery } from "gqlClient";
import { collect } from "helpers/coll";
import { enqueueSnackbar } from "notistack";
import { fetchPossibleChangesFromAutoScripts } from "./case";
import { fetchLensHome } from "./clientLens";

export interface FormState {
  form: Form | undefined;
  status: RequestStatus;
  reviewConfigByField: Record<number, ReviewConfig>;
}

const initialState: FormState = {
  form: undefined as Form | undefined,
  status: RequestStatus.IDLE,
  reviewConfigByField: {},
};

export const fetchForm = createAsyncThunk<Form, { lensId: number }>("form/fetchForm", ({ lensId }) =>
  runGqlQuery(GetFullFormDocument, { lensId })
    .then((resp) => resp.lens.formQueries.form)
    .catch((e) => {
      enqueueSnackbar("Error loading form", { variant: "error" });
      throw e;
    })
);

export const fetchReviewConfig = createAsyncThunk<
  [number, ReviewConfig],
  { reviewConfigId: number; fieldId: number },
  { state: RootState }
>("form/fetchReviewConfig", ({ reviewConfigId, fieldId }, thunkAPI) => {
  const state = thunkAPI.getState();
  const config = state.lensform.reviewConfigByField[fieldId];
  if (config !== undefined) {
    return Promise.resolve([fieldId, config]);
  }
  return runGqlQuery(GetReviewConfigDocument, { id: reviewConfigId }).then((r) => [fieldId, r.reviewConfig]);
});

const formSlice = createSlice({
  name: "lensform",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchForm.pending, (state) => {
        state.status = RequestStatus.PENDING;
      })
      .addCase(fetchForm.fulfilled, (state, action) => {
        state.status = RequestStatus.SUCCEEDED;
        state.form = action.payload;
      })
      .addCase(fetchForm.rejected, (state) => {
        state.status = RequestStatus.FAILED;
      });

    builder.addCase(fetchLensHome.fulfilled, (state, { payload: { lens } }) => {
      state.form = lens.formQueries.form;
      state.status = RequestStatus.SUCCEEDED;
    });

    builder.addCase(fetchReviewConfig.fulfilled, (state, action) => {
      const [fieldId, reviewConfig] = action.payload;
      state.reviewConfigByField[fieldId] = reviewConfig;
    });

    builder.addCase(fetchPossibleChangesFromAutoScripts.fulfilled, (state, { payload }) => {
      const form = state.form;

      if (form !== undefined) {
        form.sections = payload.lens.formQueries.form.sections;
      }
    });

    builder.addCase(USER_LOGOUT, () => {
      return { ...initialState };
    });

    return builder;
  },
});

export const selectReviewConfigByField =
  (fieldId?: number) =>
  (state: RootState): ReviewConfig | undefined =>
    fieldId ? state.lensform.reviewConfigByField[fieldId] : undefined;

export const selectReviewConfigsByField = (state: RootState): Record<number, ReviewConfig> =>
  state.lensform.reviewConfigByField;

export const selectForm = (state: RootState): Form | undefined => state.lensform.form;
export const selectFormId = (state: RootState): number | undefined => state.lensform.form?.id;
export const selectFormFields = createSelector([selectForm], (form) => form?.fields || []);
export const selectFormSections = createSelector([selectForm], (form) => form?.sections || []);
export const selectFormSectionsMap = createSelector(
  [selectFormSections],
  (sections) => new Map(sections.map((s) => [s.id, s]))
);
export const selectOutcomeEnabledFormSections = createSelector([selectFormSections], (formSections) =>
  formSections.filter((fs) => fs.outcomeEnabled)
);
export const selectOutcomeDisabledFormSections = createSelector([selectFormSections], (formSections) =>
  formSections.filter((fs) => !fs.outcomeEnabled)
);
export const selectFormPages = createSelector([selectForm], (form) => form?.pages || []);
export const selectFormCells = createSelector([selectFormFields], (formFields): Cell[] =>
  formFields.reduce((prev, field) => {
    prev.push(...field.cells);
    return prev;
  }, [] as Cell[])
);
export const selectFormPagesVisible = createSelector([selectFormPages], (pages) => pages.filter((p) => !p.isHidden));
export const selectFormFieldReviewConfigIds = createSelector([selectForm], (form) =>
  collect<FormField, { fieldId: number; configId: number }>(form?.fields || [], (field) => {
    return field.reviewConfigId
      ? {
          fieldId: field.id,
          configId: field.reviewConfigId,
        }
      : undefined;
  })
);

export default formSlice.reducer;
